import React, { useEffect, useState } from 'react';
import { Card, Typography, Space, Spin, Row, Col } from 'antd';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { analyticsApi } from '../services/api';

const { Title } = Typography;

interface SpecialtyData {
  name: string;
  value: number;
  percentage?: string;
}

interface SpecialtyDistributionChartProps {
  selectedDate: string;
}

const SpecialtyDistributionChart: React.FC<SpecialtyDistributionChartProps> = ({ selectedDate }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [specialtyData, setSpecialtyData] = useState<SpecialtyData[]>([]);

  // 颜色配置
  const colors = [
    '#1890ff', // 蓝色
    '#52c41a', // 绿色  
    '#faad14', // 金色
    '#f5222d', // 红色
    '#722ed1', // 紫色
    '#fa8c16', // 橙色
    '#13c2c2', // 青色
    '#eb2f96', // 洋红色
    '#fadb14', // 黄色
    '#a0d911'  // 青绿色
  ];

  useEffect(() => {
    if (selectedDate) {
      loadSpecialtyDistribution();
    }
  }, [selectedDate]);

  const loadSpecialtyDistribution = async () => {
    try {
      setLoading(true);
      // 获取专业分布数据
      const response = await analyticsApi.getUserSpecialtyDistribution(selectedDate);
      
      // 计算总数和百分比
      const total = response.specialties.reduce((sum: number, item: any) => sum + item.value, 0);
      
      // 转换数据格式并添加颜色
      const data: SpecialtyData[] = response.specialties
        .map((item: any, index: number) => ({
          name: item.name,
          value: item.value,
          percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : '0.0',
          color: colors[index % colors.length]
        }))
        .sort((a: any, b: any) => b.value - a.value); // 按数量降序排序

      setSpecialtyData(data);
      console.log('专业分布数据:', data);
    } catch (error) {
      console.error('Failed to load specialty distribution:', error);
      setSpecialtyData([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>ユーザー専門分布</Title>
          <span style={{ color: '#999', fontSize: '14px' }}>User Specialty Distribution</span>
        </Space>
      }
      style={{ height: '480px' }}
    >
      <div style={{ height: 'calc(100% - 60px)', padding: '16px' }}>
        <Spin spinning={loading}>
          {!loading && specialtyData.length > 0 ? (
            <Row gutter={[16, 16]} style={{ height: '100%' }}>
              <Col xs={24} sm={14} style={{ height: '100%' }}>
                <div style={{ 
                  height: '100%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center'
                }}>
                  <ResponsiveContainer width="100%" height={360}>
                    <PieChart>
                      <Pie
                        data={specialtyData}
                        cx="50%"
                        cy="50%"
                        outerRadius={130}
                        innerRadius={65}
                        dataKey="value"
                        label={false}
                      >
                        {specialtyData.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        content={({ active, payload }: any) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                padding: '8px 12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{data.name}</div>
                                <div style={{ color: '#666' }}>{data.value}人 ({data.percentage}%)</div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Col>
              <Col xs={24} sm={10} style={{ height: '100%' }}>
                <div style={{ padding: '8px 0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Title level={5} style={{ marginBottom: '8px', fontSize: '14px' }}>専門別ユーザー数</Title>
                  <div style={{ 
                    flex: 1, 
                    overflowY: 'auto', 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '4px',
                    alignContent: 'start'
                  }}>
                    {specialtyData.map((item: any, index: number) => (
                      <div key={item.name} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 6px',
                        background: index === 0 ? '#f6ffed' : '#fafafa',
                        borderRadius: '3px',
                        border: index === 0 ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
                        marginBottom: '2px',
                        minHeight: '28px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: item.color,
                            borderRadius: '2px',
                            marginRight: '3px',
                            flexShrink: 0
                          }}></div>
                          <span style={{ 
                            fontSize: '10px', 
                            lineHeight: '1.2',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>{item.name}</span>
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: '4px', flexShrink: 0 }}>
                          <div style={{ fontSize: '10px', fontWeight: 'bold' }}>
                            {item.value}人
                          </div>
                          <div style={{ fontSize: '9px', color: '#666' }}>
                            {item.percentage}%
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <div style={{ 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#999'
            }}>
              {loading ? '読み込み中...' : 'データがありません'}
            </div>
          )}
        </Spin>
      </div>
    </Card>
  );
};

export default SpecialtyDistributionChart;